// The browser side of "private keys are never stored in plain text" (public ledger concern 7).
//
// A profile's public ledger private key lives in localStorage only as ciphertext, encrypted with an AES-GCM key that the
// browser creates once, as non-extractable, and keeps in IndexedDB.
//
// What that protects against: script on our page can't export the AES key (exportKey refuses a non-extractable key), and
// whoever reads only localStorage (a stray dump of it, a localStorage-only leak) gets ciphertext without the key to it.
// What it does not: someone with a copy of the browser profile on disk has both stores, since IndexedDB holds the key's
// raw bytes in the profile, so they can decrypt offline. Nor does it stop code running on our own page, which can ask
// the browser to decrypt. Protecting against a copied profile would need a secret the user holds (a PIN through PBKDF2,
// or a passkey); we decided against that for now, so there is no PIN.
//
// It also keeps each profile's device key pair (#24 phase 3), and forgets a profile's wallet when its device is signed out.
//
// The pure parts (the entry format, telling a plain entry from an encrypted one, the storage keys, the per-profile move,
// which entries still need the wallet key, when another tab's change means forgetting) are unit tested; the Web Crypto,
// IndexedDB and localStorage calls run only in the browser.

import { timeoutSignal } from './async.js';

// An encrypted entry: the prefix, then the IV and the ciphertext, each base64
const ENTRY_PREFIX = 'nsenc:v1:';

// A profile's device key pair, in the same IndexedDB store as the wallet key
const DEVICE_KEY_PREFIX = 'device-key-v1:';

export type WalletEntryState = 'NONE' | 'PLAIN' | 'ENCRYPTED';

export interface EncryptedEntryParts {
  iv: Uint8Array;
  ciphertext: Uint8Array;
}

/** Whether a stored value is missing, a plain-text private key (from before encryption), or an encrypted entry. */
export function walletEntryState(value: string | null | undefined): WalletEntryState {
  if (!value) return 'NONE';
  return value.startsWith(ENTRY_PREFIX) ? 'ENCRYPTED' : 'PLAIN';
}

export function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

export function base64ToBytes(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/** The stored form of an encrypted value. */
export function formatEncryptedEntry(parts: EncryptedEntryParts): string {
  return `${ENTRY_PREFIX}${bytesToBase64(parts.iv)}:${bytesToBase64(parts.ciphertext)}`;
}

/** The IV and ciphertext of a stored encrypted entry, or null when it isn't one or is damaged. */
export function parseEncryptedEntry(value: string | null | undefined): EncryptedEntryParts | null {
  if (walletEntryState(value) !== 'ENCRYPTED') return null;
  const [iv, ciphertext, ...rest] = (value as string).slice(ENTRY_PREFIX.length).split(':');
  if (!iv || !ciphertext || rest.length > 0) return null;
  try {
    const parts = { iv: base64ToBytes(iv), ciphertext: base64ToBytes(ciphertext) };
    // AES-GCM's IV is 12 bytes, and the ciphertext carries a 16-byte tag
    if (parts.iv.length !== 12 || parts.ciphertext.length < 16) return null;
    return parts;
  } catch {
    return null;
  }
}

// The localStorage keys of a profile's wallet on this device, prefixed with the profile's id. Older versions prefixed them
// with the user's id; such an entry moves once to its profile (walletMigrationTarget)
const WALLET_SUFFIXES = {
  accountId: '_PUBLIC_LEDGER_ACCOUNT_ID',
  publicKey: '_PUBLIC_LEDGER_PUBLIC_KEY',
  // The private key: an encrypted entry, or a plain key left by an older version until it is migrated
  privateKey: '_PUBLIC_LEDGER_PRIVATE_KEY',
  deviceId: '_PUBLIC_LEDGER_DEVICE_ID',
  // A key pair generated for an account creation the server hasn't finished (a first director's set-up that stopped part
  // way): kept, encrypted, so trying again creates the account with the same key and the same recovery phrase
  pendingPublicKey: '_PUBLIC_LEDGER_PENDING_PUBLIC_KEY',
  pendingPrivateKey: '_PUBLIC_LEDGER_PENDING_PRIVATE_KEY',
  pendingMnemonic: '_PUBLIC_LEDGER_PENDING_MNEMONIC',
} as const;

export type WalletStorageKey = keyof typeof WALLET_SUFFIXES;

// The entries encrypted with this browser's wallet key: while one of them is left, the key is still needed
const ENCRYPTED_KEYS: WalletStorageKey[] = ['privateKey', 'pendingPrivateKey', 'pendingMnemonic'];

export function walletStorageKey(prefix: string, key: WalletStorageKey): string {
  return `${prefix}${WALLET_SUFFIXES[key]}`;
}

/** Every localStorage key of a profile's wallet on this device. */
export function walletStorageKeys(prefix: string): string[] {
  return (Object.keys(WALLET_SUFFIXES) as WalletStorageKey[]).map((key) => walletStorageKey(prefix, key));
}

/** The prefix (a profile's or, from an older version, a user's id) and entry a localStorage key is, or null for any other key. */
export function parseWalletStorageKey(storageKey: string): { prefix: string; key: WalletStorageKey } | null {
  for (const key of Object.keys(WALLET_SUFFIXES) as WalletStorageKey[]) {
    const suffix = WALLET_SUFFIXES[key];
    if (storageKey.endsWith(suffix) && storageKey.length > suffix.length) {
      return { prefix: storageKey.slice(0, -suffix.length), key };
    }
  }
  return null;
}

/**
 * Whether any of these localStorage keys is a wallet entry this browser's wallet key decrypts (of any profile or user):
 * the shared key is deleted only when none is left (DECISION 3.3).
 */
export function walletKeyStillNeeded(storageKeys: Iterable<string>): boolean {
  for (const storageKey of storageKeys) {
    const parsed = parseWalletStorageKey(storageKey);
    if (parsed && ENCRYPTED_KEYS.includes(parsed.key)) return true;
  }
  return false;
}

export interface MigrationProfile {
  id: string;
  publicKey: string | null | undefined;
}

/**
 * The profile a user-keyed wallet (from before wallets were per profile) moves to: the one whose public ledger public key
 * is the wallet's (or, for a set-up that stopped part way, its pending key's), else the user's only profile. Null when it
 * can't tell, and the entry stays where it is.
 * @param same compares two public keys whatever their encoding
 */
export function walletMigrationTarget(
  stored: { publicKey: string | null; pendingPublicKey: string | null },
  profiles: MigrationProfile[],
  same: (a: string | null | undefined, b: string | null | undefined) => boolean,
): string | null {
  for (const key of [stored.publicKey, stored.pendingPublicKey]) {
    if (!key) continue;
    const match = profiles.find((profile) => same(profile.publicKey, key));
    if (match) return match.id;
  }
  return profiles.length === 1 ? profiles[0].id : null;
}

/**
 * Whether a localStorage change from another tab means this tab forgets the profile's wallet: its private key entry was
 * removed (that tab's device was signed out), or the whole store was cleared (key null).
 */
export function shouldForgetOnStorageEvent(
  event: { key: string | null; newValue: string | null },
  profileId: string | null | undefined,
): boolean {
  if (!profileId) return false;
  if (event.key === null) return true;
  return event.key === walletStorageKey(profileId, 'privateKey') && !event.newValue;
}

/**
 * Whether a localStorage change from another tab may mean that tab deleted this browser's wallet key: an entry the key
 * decrypts was removed (of any profile or user), or the whole store was cleared (key null). This tab then forgets its
 * cached copy of the key, so it reads the key from IndexedDB again rather than using one that may be gone.
 */
export function walletKeyCacheStale(event: { key: string | null; newValue: string | null }): boolean {
  if (event.key === null) return true;
  if (event.newValue) return false;
  const parsed = parseWalletStorageKey(event.key);
  return !!parsed && ENCRYPTED_KEYS.includes(parsed.key);
}

/** The IndexedDB id of a profile's device key pair, the one that proves a signature comes from this device (#24 item 14). */
export function deviceKeyId(profileId: string): string {
  return `${DEVICE_KEY_PREFIX}${profileId}`;
}

/**
 * Whether the device row this browser holds the id of has a device key enrolled that isn't this browser's key pair (the
 * pair was lost, e.g. IndexedDB was cleared, and a new one made): the server never replaces an enrolled key, so that row
 * can never be proven again and the device enrols a new one. A row with no key yet takes this browser's.
 * @param enrolled the row's publicKey, hex
 * @param own this browser's device public key, hex
 */
export function deviceKeyReplaced(enrolled: string | null | undefined, own: string | null | undefined): boolean {
  if (!enrolled || !own) return false;
  return enrolled.toLowerCase() !== own.toLowerCase();
}

export function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * The proof sent with each signature that it comes from this device: a fresh challenge (public-ledger/device-challenge)
 * signed with the device key, signature hex
 */
export interface DeviceProof {
  deviceId: string;
  challengeId: string;
  signature: string;
}

/** The text of a device challenge the server answers (public-ledger/device-challenge), as its bytes are signed. */
export function deviceChallengeBytes(challenge: string): Uint8Array {
  return new TextEncoder().encode(challenge);
}

/*************************************************
 * Browser only: Web Crypto and IndexedDB
 *************************************************/

const DB_NAME = 'ns-public-ledger-wallet';
const STORE_NAME = 'keys';
const WALLET_KEY_ID = 'wallet-key-v1';

// The wallet key as last read from IndexedDB, for decrypting. Forgotten when another tab may have deleted it
// (walletKeyCacheStale) or a decryption with it fails; encrypting never uses it (encryptAndStoreWalletSecret)
let walletKeyPromise: Promise<CryptoKey | undefined> | null = null;

// Held across tabs while a wallet entry is encrypted and stored, and while the wallet key is checked for and deleted, so
// the key is never deleted between an entry's encryption and its being stored
const WALLET_KEY_LOCK = 'ns-public-ledger-wallet-key';

function withWalletKeyLock<T>(run: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks) return run();
  return locks.request(WALLET_KEY_LOCK, run);
}

// Held across tabs while one of them asks the server about a profile's device and re-enrols it, so a tab never asks
// about a row another tab is replacing, and two tabs that both found the device key pair replaced don't both enrol in
// place of the same row (the second would find it signed out by the first). A tab that waits longer than `waitMs` for it
// (another tab stalled holding it) gives up, rejecting, so its device check fails and is tried again later
export function withDeviceEnrolLock<T>(profileId: string, run: () => Promise<T>, waitMs?: number): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks) return run();
  const name = `ns-public-ledger-device-enrol:${profileId}`;
  const signal = waitMs === undefined ? undefined : timeoutSignal(waitMs);
  return signal ? locks.request(name, { signal }, run) : locks.request(name, run);
}

function openKeyDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE_NAME);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Unable to open the wallet key store'));
  });
}

function keyDbRequest<T>(
  db: IDBDatabase,
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const request = run(db.transaction(STORE_NAME, mode).objectStore(STORE_NAME));
    request.onsuccess = () => resolve(request.result as T);
    request.onerror = () => reject(request.error ?? new Error('Wallet key store request failed'));
  });
}

async function loadOrCreateWalletKey(): Promise<CryptoKey> {
  const db = await openKeyDb();
  try {
    const existing = await keyDbRequest<CryptoKey | undefined>(db, 'readonly', (store) => store.get(WALLET_KEY_ID));
    if (existing) return existing;
    // Non-extractable: page script can encrypt and decrypt with it but not export it (a copy of the profile can still read it)
    const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
    // add, not put: if another tab stored a key first, this fails and that key is read back instead
    try {
      await keyDbRequest(db, 'readwrite', (store) => store.add(key, WALLET_KEY_ID));
      return key;
    } catch {
      const stored = await keyDbRequest<CryptoKey | undefined>(db, 'readonly', (store) => store.get(WALLET_KEY_ID));
      if (!stored) throw new Error('Unable to store the wallet key');
      return stored;
    }
  } finally {
    db.close();
  }
}

async function loadWalletKey(): Promise<CryptoKey | undefined> {
  const db = await openKeyDb();
  try {
    return await keyDbRequest<CryptoKey | undefined>(db, 'readonly', (store) => store.get(WALLET_KEY_ID));
  } finally {
    db.close();
  }
}

// The wallet key to decrypt with: the cached copy, else read from IndexedDB (never made: a key made now decrypts nothing).
// A missing key isn't cached, so a key another tab makes later is found
function cachedWalletKey(): Promise<CryptoKey | undefined> {
  if (!walletKeyPromise) {
    const read = loadWalletKey().then(
      (key) => {
        if (!key && walletKeyPromise === read) walletKeyPromise = null;
        return key;
      },
      (err) => {
        if (walletKeyPromise === read) walletKeyPromise = null;
        throw err;
      },
    );
    walletKeyPromise = read;
  }
  return walletKeyPromise;
}

/**
 * Encrypts a secret with this browser's wallet key, read from IndexedDB (or made, when there is none) rather than any
 * cached copy, and stores the entry at `storageKey` in localStorage, all under the cross-tab wallet-key lock: another tab
 * forgetting a wallet can't delete the key between the encryption and the store, so no entry is stored under a key that
 * is gone. With `replaces`, the entry is stored only if `storageKey` still holds that value, and null is returned if not.
 * @returns the stored entry
 */
export async function encryptAndStoreWalletSecret(storageKey: string, secret: string): Promise<string>;
export async function encryptAndStoreWalletSecret(
  storageKey: string,
  secret: string,
  replaces: string,
): Promise<string | null>;
export async function encryptAndStoreWalletSecret(
  storageKey: string,
  secret: string,
  replaces?: string,
): Promise<string | null> {
  return withWalletKeyLock(async () => {
    const key = await loadOrCreateWalletKey();
    walletKeyPromise = Promise.resolve(key);
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, new TextEncoder().encode(secret));
    const entry = formatEncryptedEntry({ iv, ciphertext: new Uint8Array(ciphertext) });
    if (replaces !== undefined && localStorage.getItem(storageKey) !== replaces) return null;
    localStorage.setItem(storageKey, entry);
    return entry;
  });
}

async function decryptWith(key: CryptoKey | undefined, parts: EncryptedEntryParts): Promise<string> {
  if (!key) throw new Error('This browser has no public ledger wallet key');
  const plain = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: parts.iv as BufferSource },
    key,
    parts.ciphertext as BufferSource,
  );
  return new TextDecoder().decode(plain);
}

/**
 * Decrypts a stored entry with this browser's wallet key. A plain entry (not yet migrated) is returned as it is. A cached
 * key that fails (another tab deleted it and made a new one) is read from IndexedDB again and tried once more.
 */
export async function decryptWalletSecret(entry: string): Promise<string> {
  const state = walletEntryState(entry);
  if (state === 'PLAIN') return entry;
  const parts = parseEncryptedEntry(entry);
  if (!parts) throw new Error('The public ledger key stored on this device is damaged');
  try {
    return await decryptWith(await cachedWalletKey(), parts);
  } catch {
    resetWalletKeyCache();
    return await decryptWith(await cachedWalletKey(), parts);
  }
}

/** Forgets the cached wallet key, so the next use reads it from IndexedDB again: after it is deleted, here or in another tab. */
export function resetWalletKeyCache() {
  walletKeyPromise = null;
}

async function deleteFromKeyDb(id: string) {
  const db = await openKeyDb();
  try {
    await keyDbRequest(db, 'readwrite', (store) => store.delete(id));
  } finally {
    db.close();
  }
}

// Deletes the wallet key if no wallet entry is left to decrypt, checked in the same IndexedDB transaction as the delete
// (and, by the caller, under the wallet-key lock)
async function deleteWalletKeyIfUnneeded() {
  const db = await openKeyDb();
  try {
    await keyDbRequest(db, 'readwrite', (store) =>
      walletKeyStillNeeded(Object.keys(localStorage)) ? store.count(WALLET_KEY_ID) : store.delete(WALLET_KEY_ID),
    );
  } finally {
    db.close();
  }
}

/*************************************************
 * Browser only: the device key pair
 *************************************************/

// A non-extractable ECDSA P-256 key pair per profile, made when the device enrols (account set-up, recovery, or the first
// load of a device that has none). Its public key is registered on the device's plDevice row; each signature carries a
// fresh server challenge signed with its private key, so a signature can't be sent with another device's id.

async function loadDeviceKeyPair(profileId: string): Promise<CryptoKeyPair | undefined> {
  const db = await openKeyDb();
  try {
    return await keyDbRequest<CryptoKeyPair | undefined>(db, 'readonly', (store) => store.get(deviceKeyId(profileId)));
  } finally {
    db.close();
  }
}

async function loadOrCreateDeviceKeyPair(profileId: string): Promise<CryptoKeyPair> {
  const existing = await loadDeviceKeyPair(profileId);
  if (existing) return existing;
  // Non-extractable: page script can sign with the private key but not export it
  const pair = await crypto.subtle.generateKey({ name: 'ECDSA', namedCurve: 'P-256' }, false, ['sign', 'verify']);
  const db = await openKeyDb();
  try {
    // add, not put: if another tab stored a pair first, this fails and that pair is read back instead
    try {
      await keyDbRequest(db, 'readwrite', (store) => store.add(pair, deviceKeyId(profileId)));
      return pair;
    } catch {
      const stored = await keyDbRequest<CryptoKeyPair | undefined>(db, 'readonly', (store) =>
        store.get(deviceKeyId(profileId)),
      );
      if (!stored) throw new Error('Unable to store the device key');
      return stored;
    }
  } finally {
    db.close();
  }
}

/** The public key of the profile's device key pair (made on first use): hex of the raw uncompressed point, 130 characters. */
export async function devicePublicKeyHex(profileId: string): Promise<string> {
  const pair = await loadOrCreateDeviceKeyPair(profileId);
  return bytesToHex(new Uint8Array(await crypto.subtle.exportKey('raw', pair.publicKey)));
}

/**
 * Signs a server challenge with the profile's device key: ECDSA P-256 over SHA-256 of its UTF-8 bytes, as WebCrypto's
 * IEEE P1363 r || s, in hex (128 characters). Throws when this device has no key pair for the profile.
 */
export async function signDeviceChallenge(profileId: string, challenge: string): Promise<string> {
  const pair = await loadDeviceKeyPair(profileId);
  if (!pair) throw new Error('This device has no device key');
  const signature = await crypto.subtle.sign(
    { name: 'ECDSA', hash: 'SHA-256' },
    pair.privateKey,
    deviceChallengeBytes(challenge) as BufferSource,
  );
  return bytesToHex(new Uint8Array(signature));
}

/*************************************************
 * Browser only: moving and forgetting wallets
 *************************************************/

/**
 * Moves a user-keyed wallet (from before wallets were per profile) to its profile, once. An entry the profile already has
 * is kept and the user-keyed copy dropped. Nothing moves when the profile can't be told (walletMigrationTarget).
 */
export function migrateUserWallet(
  userId: string,
  profiles: MigrationProfile[],
  same: (a: string | null | undefined, b: string | null | undefined) => boolean,
) {
  const userKeys = walletStorageKeys(userId);
  if (!userKeys.some((key) => localStorage.getItem(key) !== null)) return;
  const target = walletMigrationTarget(
    {
      publicKey: localStorage.getItem(walletStorageKey(userId, 'publicKey')),
      pendingPublicKey: localStorage.getItem(walletStorageKey(userId, 'pendingPublicKey')),
    },
    profiles,
    same,
  );
  if (!target || target === userId) return;
  for (const key of Object.keys(WALLET_SUFFIXES) as WalletStorageKey[]) {
    const from = walletStorageKey(userId, key);
    const value = localStorage.getItem(from);
    if (value === null) continue;
    const to = walletStorageKey(target, key);
    if (localStorage.getItem(to) === null) localStorage.setItem(to, value);
    localStorage.removeItem(from);
  }
}

/**
 * Removes a profile's public ledger data from this browser (its device was signed out): its seven localStorage entries,
 * its device key pair, and this browser's wallet key once no wallet entry of any profile or user is left to decrypt
 * (DECISION 3.3). The caller forgets its copy in memory.
 */
export async function forgetWallet(profileId: string) {
  for (const key of walletStorageKeys(profileId)) localStorage.removeItem(key);
  try {
    await deleteFromKeyDb(deviceKeyId(profileId));
    // Under the lock, so no tab encrypts and stores an entry between the check and the delete
    await withWalletKeyLock(deleteWalletKeyIfUnneeded);
  } catch {
    // The localStorage entries are gone, so nothing here can be decrypted or proved with any more
  } finally {
    resetWalletKeyCache();
  }
}
